import React from "react";
import { useCurrentUserQuery } from "../../../../../../../api/queries/useUserQuery";
import {
  DOCUMENT_TYPES,
  DOC_TYPE_BY_LABEL,
} from "../../../../../../../api/patientDocuments/usePatientDocumentsQuery";
import { PERMISSIONS } from "../../../../../../../consts/api";
import { isFileManagementAllowed } from "../../../utilities";

export function useActionsRenderer({ refetchDocuments }) {
  var { data: user } = useCurrentUserQuery();

  /**
   * @param {{
   *  id: number;
   *  objectId?: number;
   *  docType: string;
   *  type: string;
   *  isLock: boolean;
   *  url: string;
   *  expireSoon: boolean;
   *  acknowledgedAt?: string;
   *  lockProcedureId?: number;
   *  gfeProcedureId?: number;
   *  isGfe?: boolean | number | string;
   *  gfe?: string;
   *  canEdit: boolean;
   *  name: string;
   *  date: string;
   *  expirationDateEditor: string;
   *  lastEditedExpirationDate: string;
   *  expirationDate: string;
   * }} row
   */
  return (row, { Download, Timer, Edit, Delete, Gfe, Lock }) => {
    var type = row.docType || DOC_TYPE_BY_LABEL[row.type];
    var permissions = user?.privileges || [];

    var hasMutatePermission = permissions.includes(
      PERMISSIONS.patientDocuments.mutate,
    );
    var canMutate = hasMutatePermission && row.canEdit;

    var canView = permissions.includes(PERMISSIONS.patientDocuments.view);
    var canMutateOrView = canMutate || canView;
    var canManageFile = isFileManagementAllowed(row.name, permissions);
    var canUpdateExpiration =
      hasMutatePermission && canManageFile && !row.isLock;

    var timerProps = {
      documentId: row.id,
      expireSoon: (row.expireSoon || row.isExpired) && !row.acknowledgedAt,
      name: row.name,
      date: row.date,
      type,
      expirationDateEditor: row.expirationDateEditor,
      lastEditedExpirationDate: row.lastEditedExpirationDate,
      expirationDate: row.expirationDate,
      typeLabel: row.type,
    };

    var editProps = {
      id: row.id,
    };

    var deleteProps = {
      id: row.id,
    };

    var lockProps = {
      isLock: row.isLock,
      procedureId: row.lockProcedureId,
    };

    var downloadProps = {
      url: row.url,
    };

    var gfeProps = {
      procedureId: row.gfeProcedureId,
      isMarkedAsGfe: row.isGfe === true || row.isGfe === 1 || row.isGfe === "1",
      gfeIconColor: row.gfe,
      refetchDocuments,
    };

    var actions = [];

    if (type === DOCUMENT_TYPES.file && canView) {
      if (!row.isLock) {
        if (canManageFile) {
          actions.push(<Download key={0} {...downloadProps} />);
        }
      } else {
        actions.push(
          <Download key={2} disabled={true} />,
          <Lock key={4} {...lockProps} />,
        );
      }
    }

    if (
      ![
        DOCUMENT_TYPES.file,
        DOCUMENT_TYPES.agreement,
        DOCUMENT_TYPES.consent,
      ].includes(type)
    ) {
      if (canView) {
        if (!row.isLock) {
          if (canManageFile) {
            actions.push(<Download key={7} {...downloadProps} />);
          }
        } else {
          actions.push(<Lock key={9} {...lockProps} />);
        }
      } else {
        actions.push(<Download key={10} disabled={true} />);
      }
    }

    if (
      [DOCUMENT_TYPES.agreement, DOCUMENT_TYPES.consent].includes(type) &&
      canMutateOrView
    ) {
      if (!row.isLock) {
        if (canManageFile) {
          actions.push(<Download key={12} {...downloadProps} />);
        }
      } else {
        actions.push(<Lock key={14} {...lockProps} />);
      }
    }

    if (canUpdateExpiration) {
      actions.push(<Timer key="timer" {...timerProps} />);
    }

    if (
      type === DOCUMENT_TYPES.procedure &&
      canView &&
      !row.isLock &&
      row.gfeProcedureId &&
      !!row.gfe
    ) {
      actions.push(<Gfe key={15} {...gfeProps} />);
    }

    if (canMutate && canManageFile) {
      actions.push(
        <Edit key="edit" {...editProps} />,
        <Delete key="delete" {...deleteProps} />,
      );
    }

    return actions;
  };
}
